import { Injectable, inject } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

/**
 * LoadingInterceptor
 *
 * Automatically manages loading state for HTTP requests.
 * Uses LoadingService to track global and per-request loading states.
 *
 * Features:
 * - Automatic loading state management
 * - Per-request loading keys based on URL
 * - Excludes certain URLs from loading tracking
 * - Properly handles concurrent requests
 */
@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private loadingService = inject(LoadingService);

  /**
   * URLs that should NOT trigger loading indicator
   * (background requests, polling, etc.)
   */
  private excludedUrls = [
    'health',
    'ping',
    'notifications/unread-count'
  ];

  /**
   * Track active requests to handle concurrent requests properly
   */
  private activeRequests = 0;

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check if this request should be excluded from loading
    if (this.shouldExclude(req)) {
      return next.handle(req);
    }

    // Get loading key from request (allows component-specific loading states)
    const loadingKey = this.getLoadingKey(req);

    // Start loading
    this.activeRequests++;
    this.loadingService.start(loadingKey);
    this.loadingService.start('global');

    return next.handle(req).pipe(
      finalize(() => {
        // Stop loading for this request
        this.loadingService.stop(loadingKey);

        // Only stop global loading when all requests complete
        this.activeRequests--;
        if (this.activeRequests === 0) {
          this.loadingService.stop('global');
        }
      })
    );
  }

  /**
   * Check if request should be excluded from loading tracking
   */
  private shouldExclude(req: HttpRequest<unknown>): boolean {
    return this.excludedUrls.some(url => req.url.includes(url));
  }

  /**
   * Generate a loading key from the request
   * This allows components to track loading for specific resources
   */
  private getLoadingKey(req: HttpRequest<unknown>): string {
    // Try to extract resource name from URL
    const urlParts = req.url.split('/').filter(Boolean);
    const apiIndex = urlParts.findIndex(part => part === 'api');

    if (apiIndex >= 0 && urlParts.length > apiIndex + 1) {
      const resource = urlParts[apiIndex + 1];
      return `http:${resource}`;
    }

    // Fallback to method + path hash
    return `http:${req.method}:${this.hashCode(req.url)}`;
  }

  /**
   * Simple hash code for strings
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }
}
